/**
 * Group store using Zustand.
 * Manages group chats, members, and messages.
 */
import { create } from 'zustand';
import type {
  GroupWithLastMessage,
  GroupDetail,
  GroupMessage,
  GroupMember,
  CreateGroupData,
  GroupRole,
} from '../types';
import { api } from '../api/client';
import { sendEngine } from '../services/queue/engine';

interface GroupState {
  // State
  groups: GroupWithLastMessage[];
  currentGroup: GroupDetail | null;
  currentMessages: GroupMessage[];
  messageCursor: number | null;
  hasMoreMessages: boolean;
  isLoading: boolean;
  isLoadingMessages: boolean;
  error: string | null;

  // Actions - Groups
  fetchGroups: () => Promise<void>;
  fetchGroup: (groupId: number) => Promise<GroupDetail | null>;
  createGroup: (data: CreateGroupData) => Promise<GroupDetail | null>;
  updateGroup: (groupId: number, data: { name?: string; description?: string; allow_member_invite?: boolean }) => Promise<boolean>;
  deleteGroup: (groupId: number) => Promise<boolean>;
  leaveGroup: (groupId: number) => Promise<boolean>;

  // Actions - Members
  addMembers: (groupId: number, userIds: number[]) => Promise<GroupMember[]>;
  removeMember: (groupId: number, userId: number) => Promise<boolean>;
  updateMemberRole: (groupId: number, userId: number, role: GroupRole) => Promise<boolean>;

  // Actions - Messages
  fetchMessages: (groupId: number, loadMore?: boolean) => Promise<void>;
  viewMessage: (groupId: number, messageId: number) => Promise<void>;
  deleteMessage: (groupId: number, messageId: number) => Promise<boolean>;

  // Actions - UI
  setCurrentGroup: (group: GroupDetail | null) => void;
  clearMessages: () => void;
  clearError: () => void;
}

export const useGroupStore = create<GroupState>((set, get) => ({
  // Initial state
  groups: [],
  currentGroup: null,
  currentMessages: [],
  messageCursor: null,
  hasMoreMessages: true,
  isLoading: false,
  isLoadingMessages: false,
  error: null,

  // Fetch all groups user is a member of
  fetchGroups: async () => {
    set({ isLoading: true, error: null });

    const response = await api.get<GroupWithLastMessage[]>('/groups');

    if (response.success && response.data) {
      set({ groups: response.data, isLoading: false });
    } else {
      set({
        isLoading: false,
        error: response.error?.message || 'Failed to fetch groups',
      });
    }
  },

  // Fetch a specific group with full details
  fetchGroup: async (groupId: number): Promise<GroupDetail | null> => {
    set({ isLoading: true, error: null });

    const response = await api.get<GroupDetail>(`/groups/${groupId}`);

    if (response.success && response.data) {
      set({ currentGroup: response.data, isLoading: false });
      return response.data;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to fetch group',
    });
    return null;
  },

  // Create a new group
  createGroup: async (data: CreateGroupData): Promise<GroupDetail | null> => {
    set({ isLoading: true, error: null });

    const response = await api.post<GroupDetail>('/groups', data);

    if (response.success && response.data) {
      // Refresh groups list
      get().fetchGroups();
      set({ isLoading: false });
      return response.data;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to create group',
    });
    return null;
  },

  // Update group info
  updateGroup: async (
    groupId: number,
    data: { name?: string; description?: string; allow_member_invite?: boolean }
  ): Promise<boolean> => {
    const response = await api.patch<GroupDetail>(`/groups/${groupId}`, data);

    if (response.success && response.data) {
      // Update current group if it's the one being updated
      if (get().currentGroup?.id === groupId) {
        set((state) => ({
          currentGroup: state.currentGroup
            ? { ...state.currentGroup, ...response.data }
            : null,
        }));
      }

      // Refresh groups list
      get().fetchGroups();
      return true;
    }

    set({ error: response.error?.message || 'Failed to update group' });
    return false;
  },

  // Delete a group
  deleteGroup: async (groupId: number): Promise<boolean> => {
    const response = await api.delete<{ deleted: boolean }>(`/groups/${groupId}`);

    if (response.success) {
      // Remove from groups list
      set((state) => ({
        groups: state.groups.filter((g) => g.id !== groupId),
      }));

      // Clear current group if it's the one being deleted
      if (get().currentGroup?.id === groupId) {
        set({ currentGroup: null, currentMessages: [] });
      }

      return true;
    }

    set({ error: response.error?.message || 'Failed to delete group' });
    return false;
  },

  // Leave a group
  leaveGroup: async (groupId: number): Promise<boolean> => {
    const response = await api.post<{ left: boolean }>(`/groups/${groupId}/leave`);

    if (response.success) {
      // Remove from groups list
      set((state) => ({
        groups: state.groups.filter((g) => g.id !== groupId),
      }));

      // Clear current group if it's the one being left
      if (get().currentGroup?.id === groupId) {
        set({ currentGroup: null, currentMessages: [] });
      }

      return true;
    }

    set({ error: response.error?.message || 'Failed to leave group' });
    return false;
  },

  // Add members to a group
  addMembers: async (groupId: number, userIds: number[]): Promise<GroupMember[]> => {
    const response = await api.post<GroupMember[]>(`/groups/${groupId}/members`, {
      user_ids: userIds,
    });

    if (response.success && response.data) {
      // Update current group's member list
      if (get().currentGroup?.id === groupId) {
        set((state) => ({
          currentGroup: state.currentGroup
            ? {
                ...state.currentGroup,
                members: [...state.currentGroup.members, ...response.data!],
                member_count: state.currentGroup.member_count + response.data!.length,
              }
            : null,
        }));
      }

      return response.data;
    }

    set({ error: response.error?.message || 'Failed to add members' });
    return [];
  },

  // Remove a member from a group
  removeMember: async (groupId: number, userId: number): Promise<boolean> => {
    const response = await api.delete<{ removed: boolean }>(
      `/groups/${groupId}/members/${userId}`
    );

    if (response.success) {
      // Update current group's member list
      if (get().currentGroup?.id === groupId) {
        set((state) => ({
          currentGroup: state.currentGroup
            ? {
                ...state.currentGroup,
                members: state.currentGroup.members.filter((m) => m.user.id !== userId),
                member_count: state.currentGroup.member_count - 1,
              }
            : null,
        }));
      }

      return true;
    }

    set({ error: response.error?.message || 'Failed to remove member' });
    return false;
  },

  // Update a member's role
  updateMemberRole: async (
    groupId: number,
    userId: number,
    role: GroupRole
  ): Promise<boolean> => {
    const response = await api.patch<GroupMember>(
      `/groups/${groupId}/members/${userId}/role`,
      { role }
    );

    if (response.success && response.data) {
      // Update current group's member list
      if (get().currentGroup?.id === groupId) {
        set((state) => ({
          currentGroup: state.currentGroup
            ? {
                ...state.currentGroup,
                members: state.currentGroup.members.map((m) =>
                  m.user.id === userId ? { ...m, role } : m
                ),
              }
            : null,
        }));
      }

      return true;
    }

    set({ error: response.error?.message || 'Failed to update role' });
    return false;
  },

  // Fetch messages for a group
  fetchMessages: async (groupId: number, loadMore = false) => {
    const { messageCursor, hasMoreMessages, currentMessages } = get();

    if (loadMore && !hasMoreMessages) return;

    set({ isLoadingMessages: true, error: null });

    const params: Record<string, string> = { limit: '50' };
    if (loadMore && messageCursor) {
      params.before = String(messageCursor);
    }

    const response = await api.get<{ messages: GroupMessage[]; cursor: number | null }>(
      `/groups/${groupId}/messages`,
      params
    );

    if (response.success && response.data) {
      const { messages, cursor } = response.data;

      set({
        currentMessages: loadMore
          ? [...currentMessages, ...messages]
          : messages,
        messageCursor: cursor,
        hasMoreMessages: cursor !== null,
        isLoadingMessages: false,
      });
    } else {
      set({
        isLoadingMessages: false,
        error: response.error?.message || 'Failed to fetch messages',
      });
    }
  },

  // Mark a message as viewed
  viewMessage: async (groupId: number, messageId: number) => {
    await api.post<{ viewed: boolean }>(
      `/groups/${groupId}/messages/${messageId}/view`
    );

    // Update local state
    set((state) => ({
      currentMessages: state.currentMessages.map((m) =>
        m.id === messageId ? { ...m, viewed: true } : m
      ),
    }));
  },

  // Delete a message
  deleteMessage: async (groupId: number, messageId: number): Promise<boolean> => {
    const response = await api.delete<{ deleted: boolean }>(
      `/groups/${groupId}/messages/${messageId}`
    );

    if (response.success) {
      // Remove from messages list
      set((state) => ({
        currentMessages: state.currentMessages.filter((m) => m.id !== messageId),
      }));

      return true;
    }

    set({ error: response.error?.message || 'Failed to delete message' });
    return false;
  },

  // Set current group
  setCurrentGroup: (group: GroupDetail | null) => {
    set({ currentGroup: group });
  },

  // Clear messages (when leaving a group chat view)
  clearMessages: () => {
    set({
      currentMessages: [],
      messageCursor: null,
      hasMoreMessages: true,
    });
  },

  // Clear error
  clearError: () => set({ error: null }),
}));

// A group send the queue delivered: refresh the group list so the last-message
// preview catches up (the open thread refetches on entry). Group media has no
// optimistic bubble to replace — it appears when the thread reloads.
sendEngine.onResult((result) => {
  if (result.row.dest.kind !== 'group' || !result.success) return;
  void useGroupStore.getState().fetchGroups();
});
